import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { formatApiError } from "../../lib/errors";

const COOLDOWN_SECONDS = 60;

export default function ResendVerification({ email, className = "" }) {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  const { t } = useTranslation("auth");

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleClick = async () => {
    if (!email || cooldown > 0 || status === "sending") return;
    setError("");
    setStatus("sending");
    try {
      await authApi.resendVerification(email);
      setStatus("sent");
      startCooldown();
    } catch (err) {
      setError(formatApiError(err, t("resendVerification.errorFallback")));
      setStatus("error");
      if (err.response?.status === 429) startCooldown();
    }
  };

  const disabled = !email || cooldown > 0 || status === "sending";
  const label =
    status === "sending"
      ? t("resendVerification.sending")
      : cooldown > 0
        ? t("resendVerification.cooldown", { seconds: cooldown })
        : t("resendVerification.button");

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className="text-sm text-[#FF5500] hover:text-[#FF7733] disabled:text-[#A1A1AA] disabled:cursor-not-allowed underline-offset-2 hover:underline"
      >
        {label}
      </button>
      {status === "sent" && (
        <p className="mt-2 text-xs text-[#A1A1AA]">
          {t("resendVerification.sentPrefix")} <span className="text-white">{email}</span>{t("resendVerification.sentSuffix")}
        </p>
      )}
      {status === "error" && error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}
